import { Client, Room, SeatReservation } from 'colyseus.js';
import { ClientClockSync } from './clock-sync';
import type { ModelInstance } from '../render/instanced-layout';

/**
 * Connects to the live match room (backend
 * src/game/dungeon-instance/rooms/dungeon-instance.room.ts). Movement/combat
 * sends (sendMove/sendSprint/sendJump/sendAttack/sendParry) are reused
 * as-is from colyseus-client.ts — they're generic over any Room, and this
 * room speaks the identical input.* protocol as test_corridor.
 */

const COLYSEUS_URL = import.meta.env.VITE_COLYSEUS_URL || `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/colyseus`;

export interface DungeonLayoutPayload {
  seed: number;
  /** Multi-stratum amendment — Run.stratum's depth convention (1=Cellars, 2=Ossuary, 3=Vaults). */
  stratum: number;
  isFallback: boolean;
  roomCount: number;
  instances: ModelInstance[];
  /** design/gdd/run-start-flythrough.md — null only in the defensive unreachable-path case (Edge Case #1). */
  flythrough: { waypoints: { x: number; z: number }[]; durationMs: number } | null;
}

export interface DungeonInstanceJoinResult {
  room: Room;
  clockSync: ClientClockSync;
  layout: DungeonLayoutPayload;
}

async function afterJoin(room: Room): Promise<DungeonInstanceJoinResult> {
  const clockSync = new ClientClockSync(room);
  clockSync.start();

  // Handler-before-request — see dungeon-preview-client.ts's comment on why (colyseus.js drops unbuffered messages).
  const layout = await new Promise<DungeonLayoutPayload>((resolve) => {
    room.onMessage('dungeon.layout', (msg: DungeonLayoutPayload) => resolve(msg));
    room.send('request.layout');
  });

  return { room, clockSync, layout };
}

/** Direct-join bypass (`/play?view=dungeon_instance`) — skips Matchmaking entirely, for dev/QA testing. */
export async function joinDungeonInstance(seed?: number): Promise<DungeonInstanceJoinResult> {
  const client = new Client(COLYSEUS_URL);
  const room = await client.joinOrCreate('dungeon_instance', seed !== undefined ? { seed } : {});
  return afterJoin(room);
}

/** design/gdd/sconce-hub.md Core Rule #5 — the real production path, via Matchmaking's `matchmaking.found` seat reservation. */
export async function joinDungeonInstanceViaReservation(reservation: SeatReservation): Promise<DungeonInstanceJoinResult> {
  const client = new Client(COLYSEUS_URL);
  const room = await client.consumeSeatReservation(reservation);
  return afterJoin(room);
}

/**
 * MMO combat integration (2026-08-24) — action-bar activation and soft-target
 * selection. Slot rather than ability id: the server owns which ability sits
 * in which slot, so the client never needs to know ability ids to play.
 */
/** Asks the server for this player's action bar — see the server handler's note on why it is client-requested. */
export function requestAbilityState(room: Room): void {
  room.send('ability.request', {});
}

export function sendAbilitySlot(room: Room, slot: number): void {
  room.send('input.ability_slot', { slot });
}

/** An empty id clears the target. */
export function sendTarget(room: Room, targetId: string): void {
  room.send('combat.target', { targetId });
}

export function sendDoorBar(room: Room, targetId: string): void {
  room.send('world.door_bar', { targetId });
}
export function sendDoorUnbar(room: Room, targetId: string): void {
  room.send('world.door_unbar', { targetId });
}
export function sendCrankStart(room: Room, targetId: string): void {
  room.send('world.portcullis_crank_start', { targetId });
}
export function sendCrankStop(room: Room, targetId: string): void {
  room.send('world.portcullis_crank_stop', { targetId });
}
/**
 * Reviving a downed squadmate (2026-08-25) — a hold, so it is a start/stop
 * pair like the portcullis crank rather than a single fire-and-forget message.
 * `clientTs` because the server's payload guard requires one, same as every
 * other timed input.
 */
export function sendReviveStart(room: Room, targetId: string): void {
  room.send('party.revive_start', { targetId, clientTs: performance.now() });
}
export function sendReviveStop(room: Room): void {
  room.send('party.revive_stop', {});
}

/**
 * Asks for the carried-loot snapshot once this client's handlers are live.
 *
 * Requested rather than pushed at join, for the reasons the server's
 * `ability.request` note records in full: a private-state send from `onJoin`
 * corrupts the schema stream, and one from the first tick races this
 * registration and is dropped.
 */
export function requestLootInventory(room: Room): void {
  room.send('loot.request', {});
}

export function sendChestOpen(room: Room, targetId: string): void {
  room.send('world.chest_open', { targetId });
}
/** Searching a body you killed (2026-09-07) — same shape as a chest, because the server treats it as one. */
export function sendCorpseLoot(room: Room, monsterId: string): void {
  room.send('world.corpse_loot', { targetId: monsterId });
}
export function sendBrazierDouse(room: Room, targetId: string): void {
  room.send('world.brazier_douse', { targetId });
}
export function sendBrazierLight(room: Room, targetId: string): void {
  room.send('world.brazier_light', { targetId });
}

/**
 * design/gdd/run-lifecycle.md Core Rule #3 — leaves through a specific,
 * currently-OPEN portcullis.
 *
 * `mode` is what the floor used to decide on the player's behalf (2026-09-07):
 * `'OUT'` banks the run here, `'DOWN'` carries the bag into the next floor and
 * keeps the run open. Defaulted to `'OUT'` at every layer — here, in the
 * server's payload guard, and in the key that is bound to it — because the
 * default has to be the one that keeps your loot.
 */
export function sendExtract(room: Room, portcullisId: string, mode: 'OUT' | 'DOWN' = 'OUT'): void {
  room.send('run.extract', { targetId: portcullisId, mode });
}

/**
 * design/gdd/waves.md Core Rule #4 — leaving, which is the only way a Waves
 * run ends well.
 *
 * Its own message rather than `run.extract` because it has no target: a delve
 * extracts *through* a portcullis it must name, and Waves extracts by standing
 * somewhere during a window. The server refuses out loud if either half is
 * missing (`wave_in_progress`, `not_on_the_stair`), so this can be sent
 * optimistically.
 */
export function sendWavesLeave(room: Room): void {
  room.send('waves.leave', {});
}

/** design/gdd/loot-inventory.md Core Rule #6 — permanently destroyed, never placed back into the world. */
export function sendLootDrop(room: Room, itemId: string): void {
  room.send('loot.drop', { itemId });
}
/** design/gdd/insurance.md Core Rules #3-#5. */
export function sendLootInsure(room: Room, itemId: string): void {
  room.send('loot.insure', { itemId });
}
export function sendLootUninsure(room: Room, itemId: string): void {
  room.send('loot.uninsure', { itemId });
}
